import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";
import { hasPermission } from "../../../utils/permissionUtils";
import {
    Box, Typography, Alert, Button, CircularProgress, Tabs, Tab, Paper,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    LinearProgress
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import ReplayIcon from "@mui/icons-material/Replay";
import SyncIcon from "@mui/icons-material/Sync";

import ListFacts from "./ListFacts";
import AddEditFactModal from "./AddEditFactModal";
import ExtractFactsModal from "./ExtractFactsModal";
import PendingReviewSection from "./PendingReviewSection";
import FactStats from "./FactStats";

export default function FactTablesPage() {
    const { chatbotId } = useParams();

    // Permissions
    const [canView, setCanView] = useState(false);
    const [canModify, setCanModify] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [checkingPermissions, setCheckingPermissions] = useState(true);

    // UI State
    const [activeTab, setActiveTab] = useState(0);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showExtractModal, setShowExtractModal] = useState(false);
    const [editData, setEditData] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Re-run Process State
    const [showRerunConfirm, setShowRerunConfirm] = useState(false);
    const [rerunLoading, setRerunLoading] = useState(false);
    const [rerunResult, setRerunResult] = useState(null);
    const [rerunError, setRerunError] = useState('');

    // Retrain State
    const [retrainLoading, setRetrainLoading] = useState(false);
    const [retrainResult, setRetrainResult] = useState(null);

    useEffect(() => {
        document.title = "Fact Tables | KlonIT Web Application";
        checkPermissions();
    }, []);

    const checkPermissions = async () => {
        try {
            const viewPerm = await hasPermission("fact-tables", "view");
            const modifyPerm = await hasPermission("fact-tables", "add|edit");
            const deletePerm = await hasPermission("fact-tables", "delete");
            setCanView(viewPerm);
            setCanModify(modifyPerm);
            setCanDelete(deletePerm);
        } catch (e) {
            console.error("Error checking permissions:", e);
        } finally {
            setCheckingPermissions(false);
        }
    };

    const triggerRefresh = () => setRefreshTrigger(prev => prev + 1);

    const handleEdit = (fact) => {
        setEditData(fact);
        setShowAddModal(true);
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
        setEditData(null);
    };

    const handleRerunProcess = async () => {
        setRerunLoading(true);
        setRerunError('');
        setRerunResult(null);

        try {
            const response = await axiosInstance.post('/api/facts/rerun', {}, {
                params: { chatbot_id: chatbotId },
                timeout: 600000 // 10 min timeout for full re-extraction
            });

            if (response.data.success) {
                setRerunResult(response.data);
                triggerRefresh();
            } else {
                setRerunError(response.data.message || 'Re-run process failed.');
            }
        } catch (err) {
            if (err.code === 'ECONNABORTED') {
                setRerunError('Re-run process timed out. The extraction may still be running in the background.');
            } else {
                setRerunError(err.response?.data?.message || err.message || 'An error occurred during re-run.');
            }
        } finally {
            setRerunLoading(false);
        }
    };

    const handleRetrain = async () => {
        setRetrainLoading(true);
        setRetrainResult(null);
        try {
            const response = await axiosInstance.post('/api/facts/retrain', {}, {
                params: { chatbot_id: chatbotId },
                timeout: 300000
            });
            if (response.data.success) {
                setRetrainResult(response.data);
            }
        } catch (err) {
            setRetrainResult({ success: false, message: err.response?.data?.message || err.message || 'Retrain failed.' });
        } finally {
            setRetrainLoading(false);
        }
    };

    const handleCloseRerunDialog = () => {
        setShowRerunConfirm(false);
        setRerunResult(null);
        setRerunError('');
    };

    if (checkingPermissions) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!canView) {
        return (
            <Alert severity="warning" sx={{ m: 2 }}>
                You do not have permission to view Fact Tables.
            </Alert>
        );
    }

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                        Fact Tables
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage structured facts with temporal validity. Facts automatically expire based on their "Good Until" date.
                    </Typography>
                </Box>
                {canModify && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        <Button
                            variant="outlined"
                            color="info"
                            startIcon={retrainLoading ? <CircularProgress size={16} /> : <SyncIcon />}
                            onClick={handleRetrain}
                            disabled={retrainLoading}
                        >
                            {retrainLoading ? 'Syncing...' : 'Sync to Chatbot'}
                        </Button>
                        <Button
                            variant="outlined"
                            color="warning"
                            startIcon={<ReplayIcon />}
                            onClick={() => setShowRerunConfirm(true)}
                            disabled={rerunLoading}
                        >
                            Re-run Process
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<AutoFixHighIcon />}
                            onClick={() => setShowExtractModal(true)}
                        >
                            Extract Facts
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => setShowAddModal(true)}
                        >
                            Add Fact
                        </Button>
                    </Box>
                )}
            </Box>

            {/* Retrain Result */}
            {retrainResult && (
                <Alert
                    severity={retrainResult.success ? "success" : "error"}
                    onClose={() => setRetrainResult(null)}
                    sx={{ mb: 2 }}
                >
                    {retrainResult.message}
                </Alert>
            )}

            {/* Stats */}
            <FactStats refresh={refreshTrigger} />

            {/* Tabs */}
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
                    <Tab label="All Facts" />
                    <Tab label="Pending Review" />
                </Tabs>

                <Box sx={{ p: 2 }}>
                    {activeTab === 0 && (
                        <ListFacts
                            refresh={refreshTrigger}
                            onEdit={handleEdit}
                            canModify={canModify}
                            canDelete={canDelete}
                        />
                    )}
                    {activeTab === 1 && (
                        <PendingReviewSection
                            refresh={refreshTrigger}
                            onResolved={triggerRefresh}
                        />
                    )}
                </Box>
            </Paper>

            {/* Modals */}
            <AddEditFactModal
                open={showAddModal}
                onClose={handleCloseModal}
                onSave={triggerRefresh}
                editData={editData}
            />
            <ExtractFactsModal
                open={showExtractModal}
                onClose={() => setShowExtractModal(false)}
                onComplete={triggerRefresh}
            />

            {/* Re-run Process Confirmation Dialog */}
            <Dialog open={showRerunConfirm} onClose={!rerunLoading ? handleCloseRerunDialog : undefined} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReplayIcon color="warning" />
                    Re-run Fact Extraction Process
                </DialogTitle>
                <DialogContent>
                    {!rerunResult && !rerunLoading && (
                        <DialogContentText>
                            This will <strong>delete all auto-extracted facts and relationships</strong> and
                            re-extract from all data sources (documents, websites, images, voice, Google Drive, OneDrive).
                            <br /><br />
                            Manually added facts and relationships will be preserved.
                            <br /><br />
                            This process may take several minutes depending on the amount of data.
                        </DialogContentText>
                    )}

                    {rerunLoading && (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                            <CircularProgress size={40} sx={{ mb: 2 }} />
                            <Typography variant="body2" color="text.secondary">
                                Clearing old data and re-extracting from all sources...
                            </Typography>
                            <LinearProgress sx={{ mt: 2 }} />
                        </Box>
                    )}

                    {rerunError && <Alert severity="error" sx={{ mt: 1 }}>{rerunError}</Alert>}

                    {rerunResult && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>Re-run Complete</Typography>
                            <Typography variant="body2">
                                Cleared: <strong>{rerunResult.cleared_facts}</strong> facts, <strong>{rerunResult.cleared_relationships}</strong> relationships<br />
                                Re-extracted: <strong>{rerunResult.facts_created}</strong> facts, <strong>{rerunResult.relationships_created}</strong> relationships<br />
                                Conflicts resolved: <strong>{rerunResult.conflicts_resolved}</strong><br />
                                Sources processed: <strong>{rerunResult.sources_processed}</strong>
                            </Typography>
                            {rerunResult.errors && rerunResult.errors.length > 0 && (
                                <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                                    {rerunResult.errors.length} source(s) had errors during processing.
                                </Typography>
                            )}
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseRerunDialog} disabled={rerunLoading}>
                        {rerunResult ? 'Close' : 'Cancel'}
                    </Button>
                    {!rerunResult && (
                        <Button
                            onClick={handleRerunProcess}
                            variant="contained"
                            color="warning"
                            disabled={rerunLoading}
                            startIcon={<ReplayIcon />}
                        >
                            {rerunLoading ? 'Processing...' : 'Confirm Re-run'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
        </Box>
    );
}
